import { useLocalization } from "@fluent/react";
import { Button, Row } from "@narmi/design_system";
import { Fragment, useEffect, useRef, useState } from "react";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { PageHeader } from "@lending/components/PageHeader";
import { useVerifyVerifiedIdentityChallengeMutation } from "@lending/hooks/queries/VerifiedIdentityChallenge/useVerifyVerifiedIdentityChallengeMutation";
import { useNavigateWithSearch } from "@lending/hooks/useNavigateWithSearch";
import { useToastsContext } from "@lending/hooks/useToasts";
import styles from "../PortalLogin.module.css";
import { usePortalLogin } from "../PortalLoginContext";

const CODE_LENGTH = 6;

/**
 * Final step: collect the emailed code and exchange it for a verified identity
 * session.
 *
 * A successful verify sets the lending session cookie, so the dashboard's
 * application queries authenticate on arrival with no token handling here.
 */
export function VerifyCodeScreen() {
  const { l10n } = useLocalization();
  const navigate = useNavigateWithSearch();
  const { challenge } = usePortalLogin();
  const { showErrorToast } = useToastsContext();
  const [values, setValues] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const { mutateAsync: verifyCode, isPending } = useVerifyVerifiedIdentityChallengeMutation();

  const code = values.join("");
  const isComplete = values.every(Boolean);

  // Internal and demo environments get the code back from the API so the flow can
  // be completed without the applicant's inbox. Production never sends it.
  const maskedEmail = challenge?.selectedMaskedEmail ?? null;
  let subheading: string;
  if (challenge?.demoCode) {
    subheading = maskedEmail
      ? l10n.getString(
          "portal-login-verify-subheading-demo-email",
          { email: maskedEmail, code: challenge.demoCode },
          `We sent a 6-digit code to your ${maskedEmail}. For this demo, enter ${challenge.demoCode}.`
        )
      : l10n.getString(
          "portal-login-verify-subheading-demo",
          { code: challenge.demoCode },
          `We sent a 6-digit code to your email address. For this demo, enter ${challenge.demoCode}.`
        );
  } else {
    subheading = maskedEmail
      ? l10n.getString(
          "portal-login-verify-subheading-email",
          { email: maskedEmail },
          `We sent a 6-digit code to your ${maskedEmail}.`
        )
      : l10n.getString(
          "portal-login-verify-subheading",
          null,
          "We sent a 6-digit code to your email address."
        );
  }

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  async function handleContinue() {
    // Enabled even while the code is short: submitting a partial code would
    // fail as "invalid or expired", which blames the code rather than saying
    // it is unfinished.
    if (!isComplete) {
      showErrorToast(
        l10n.getString("portal-login-incomplete-code", null, "Enter all 6 digits of your code")
      );
      return;
    }

    if (!challenge) {
      return;
    }

    try {
      await verifyCode({
        params: { path: { uuid: challenge.challengeId } },
        body: { code },
      });

      // Do not clear the challenge first. Clearing re-renders this step while it is
      // still mounted, `PortalLoginGuard` sees no challenge in flight, and its
      // `<Navigate replace>` back to the start beats the async navigation below.
      // Leaving `/portal/login` unmounts the layout and discards the challenge anyway.
      void navigate("/portal");
    } catch {
      showErrorToast(
        l10n.getString(
          "portal-login-invalid-code-error",
          null,
          "That code was invalid or expired. Please try again."
        )
      );
      setValues(Array(CODE_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    }
  }

  function handleChange(index: number, nextValue: string) {
    if (nextValue && !/^\d$/.test(nextValue)) {
      return;
    }

    const nextValues = [...values];
    nextValues[index] = nextValue;
    setValues(nextValues);

    if (nextValue && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Backspace" && !values[index] && index > 0) {
      event.preventDefault();
      const nextValues = [...values];
      nextValues[index - 1] = "";
      setValues(nextValues);
      inputRefs.current[index - 1]?.focus();
      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    const pastedData = event.clipboardData
      .getData("text/plain")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);

    if (!pastedData) {
      return;
    }

    const nextValues = pastedData
      .split("")
      .concat(Array(CODE_LENGTH).fill(""))
      .slice(0, CODE_LENGTH);
    setValues(nextValues);
    inputRefs.current[Math.min(pastedData.length, CODE_LENGTH) - 1]?.focus();
  }

  return (
    <>
      <PageHeader
        heading={l10n.getString("portal-login-verify-heading", null, "Enter verification code")}
        subheading={subheading}
      />

      <div className={styles.otpGroup}>
        {values.map((value, index) => (
          <Fragment key={`portal-login-verification-${index}`}>
            <input
              ref={(element) => {
                inputRefs.current[index] = element;
              }}
              aria-label={l10n.getString(
                "ui-otp-input-aria-label",
                { position: index + 1, total: CODE_LENGTH },
                `Digit ${index + 1} of ${CODE_LENGTH}`
              )}
              className={styles.otpInput}
              inputMode="numeric"
              maxLength={1}
              type="text"
              value={value}
              onChange={(event) => handleChange(index, event.target.value)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              onPaste={handlePaste}
            />
            {index === 2 && <div className={styles.separator} aria-hidden="true" />}
          </Fragment>
        ))}
      </div>

      <div className="margin--top--xxl">
        <Row alignItems="center" justifyContent="space-between">
          <BackButton
            onClick={() => {
              void navigate("/portal/login");
            }}
          />
          <Button
            label={l10n.getString("button-label-continue", null, "Continue")}
            onClick={() => void handleContinue()}
            isLoading={isPending}
          />
        </Row>
      </div>
    </>
  );
}
